import type { FunctionComponent } from 'react'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Grid, GridItem, PageTitle } from '@which/seatbelt'
import { dynamicGa4DataLayerPush } from '@which/shared'

import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import otel, { SpanStatusCode } from '@opentelemetry/api'
import classnames from 'classnames'

import type { Meta } from '../../../generated/frontend'
import { useLoginMutation } from '../../../generated/frontend'
import { getQueryString, isLocal, Loader } from '../../../shared'
import { ErrorComponent } from '../../../shared/components/Error'
import { InfoBanner, type Props } from '../../../shared/components/InfoBanner'
import { useBaseMetaTags } from '../../../shared/hooks/useBaseMetaTags'
import { paywallUrls } from '../../../shared/paywall-urls'
import { LoginPageBannerText } from '../data'
import { accountPagesDataLayer, getIsValidReferrer, setTransactionName } from '../utils'
import styles from './LoginPage.module.scss'
import { processLoginMutationResponse } from './loginUtils'
import TwoStepLoginForm from './TwoStepLoginForm'

const LoginPage: FunctionComponent = () => {
  const isKindeOnlyLogin = useFeatureIsOn('paywall-migrated-user-off')

  const [content, setContent] = useState('')

  const kinde = useKindeAuth()
  const [context, setContext] = useState<Props['context']>('error')
  const [stayLoggedIn, setStayLoggedIn] = useState(true)
  const [showInfoBanner, setShowInfoBanner] = useState(false)

  const [paywallState] = useState(paywallUrls())
  const [isLoading, setIsLoading] = useState(true)

  const [title, setTitle] = useState('')

  const { getBaseMetaTags } = useBaseMetaTags()

  const [loginMutation, { data, error, loading }] = useLoginMutation()
  setTransactionName('account/login')

  const metaTagObj = {
    canonical: 'https://www.which.co.uk/login',
    title: "Login to which.co.uk | Discover Best Buys & Don't Buys",
    fbAppId: '320949621749168',
    desc: 'From this page members can log directly into which.co.uk. Access thousands of expertly tested independent reviews to help you make confident choices. Please have your email address or username and password ready.',
  }

  const getMetaTags = ({ meta, imageUrl, twitterCard }: LoginMetaArgs) => [
    ...getBaseMetaTags({ meta, imageUrl, twitterCard }),
  ]

  const showAuthenticationError = (
    copy: (typeof LoginPageBannerText)[keyof typeof LoginPageBannerText]
  ) => {
    setShowInfoBanner(true)
    setContext('error')
    setContent(copy.authErrorContentText)
    setTitle(copy.authErrorText)
    dynamicGa4DataLayerPush({
      event: 'notification_error',
      action_group: 'login',
      item_text: copy.authErrorText,
      utagid: 'AUP4858DP01',
    })
  }

  useEffect(() => {
    if (data && data.login) {
      const { login } = data

      processLoginMutationResponse({
        login,
        stayLoggedIn,
      })

      if (login.__typename === 'AuthenticationError') {
        showAuthenticationError(LoginPageBannerText.password)
      }
    }
  }, [data, stayLoggedIn])

  useEffect(() => {
    if (document.cookie.includes('ref=reset-password')) {
      document.cookie = 'ref= ; '
      setShowInfoBanner(true)
      setContent(LoginPageBannerText.generic.Success)
      setContext('correct')
      setTitle('')
    }
  }, [])

  useEffect(() => {
    if (error) {
      if ('otel' in window) {
        const span = otel?.trace?.getActiveSpan()
        if (error instanceof Error) {
          span?.recordException(error)
        }
        span?.setAttribute('error.statusCode', 500)
        span?.setStatus({ code: SpanStatusCode.ERROR })
      }
      dynamicGa4DataLayerPush({
        event: 'notification_error',
        action_group: 'login',
        item_text: `500 error: ${error.message}`,
        utagid: 'AUP4858DP01',
      })
    }
  }, [error])

  useEffect(() => {
    const fetchKindeRedirectUrl = () => {
      let kindeRedirectUrl = window.origin
      const maybeReturnUrl = getQueryString('return_url')
      const returnUrlParamPresent = typeof maybeReturnUrl === 'string'

      const maybeReferrerPath = getQueryString('referrer_path')
      const referrerPathParamPresent = typeof maybeReferrerPath === 'string'
      const isValidReferrer = getIsValidReferrer(document?.referrer)
      if (returnUrlParamPresent) {
        kindeRedirectUrl = `${location.origin}/${maybeReturnUrl}`
      } else if (isValidReferrer && referrerPathParamPresent) {
        const referrerUrl = new URL(document.referrer)
        kindeRedirectUrl = `${referrerUrl.origin}/${maybeReferrerPath}`
      } else if (isValidReferrer) {
        kindeRedirectUrl = document.referrer
      }

      return kindeRedirectUrl
    }
    const kindeLogin = () => {
      const domain = isLocal() ? 'localhost' : '.which.co.uk'

      if (!kinde.isAuthenticated) {
        document.cookie = `sessionId=temp;path=/;domain=${domain};expires=Fri, 31 Dec 9999 23:59:59 GMT`
        document.cookie = `blaize_session=temp;path=/;domain=${domain};expires=Fri, 31 Dec 9999 23:59:59 GMT`
      }

      sessionStorage.setItem('kindeRedirect', fetchKindeRedirectUrl())

      kinde.login({
        redirectURL: paywallState.redirectFeatureUri,
      })
    }
    if (isKindeOnlyLogin) {
      kindeLogin()
    } else {
      setIsLoading(false)
    }
  }, [paywallState, kinde, isKindeOnlyLogin])

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (isLoading) {
    return <Loader />
  }
  return (
    <>
      <Helmet>
        {getMetaTags({
          meta: metaTagObj,
          imageUrl: 'https://www.which.co.uk/login',
          twitterCard: 'summary_large_image',
        })}
        <script>
          {accountPagesDataLayer({
            content_type: 'login',
          })}
        </script>
      </Helmet>

      <Grid className={classnames(styles.loginPage, showInfoBanner && styles[context])}>
        <GridItem span={{ medium: 6, large: 6 }} columnStart={{ medium: 4, large: 4 }}>
          {showInfoBanner && <InfoBanner content={content} context={context} title={title} />}
          <PageTitle pageTitle="Log into Which?" pageTitleTag="h1" />
          <TwoStepLoginForm
            loading={loading}
            showAuthenticationError={showAuthenticationError}
            handleOnSubmit={(username, password, shouldStayLoggedIn) => {
              loginMutation({ variables: { username, password } })
              setStayLoggedIn(shouldStayLoggedIn)
              setShowInfoBanner(false)
            }}
            stayLoggedInState={{ stayLoggedIn, setStayLoggedIn }}
          />
        </GridItem>
      </Grid>
    </>
  )
}

export default LoginPage

/////// IMPLEMENTATION /////////

type LoginMetaArgs = {
  meta: Partial<Meta>
  imageUrl: string
  twitterCard: string
}

interface StayLoggedInState {
  stayLoggedIn: boolean
  setStayLoggedIn: React.Dispatch<React.SetStateAction<boolean>>
}

export type LoginFormProps = {
  handleOnSubmit: (username: string, password: string, stayLoggedIn: boolean) => void
  showAuthenticationError: (copy: (typeof LoginPageBannerText)['generic']) => void
  loading: boolean
  stayLoggedInState: StayLoggedInState
}
